import {
	ActionAndOthers,
	ActionBarItemAlignment,
	ActionGroup,
	ActionKind,
	reduceToAvailableActions,
	resolveActionTitle
} from "client_legacy/ndaw/actions";
import { ActionBar, ActionBarHorizontalAlignment } from "./ActionBar";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	ensureNotNil,
	isArray,
	isNotNil
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { actionComponentFactory } from "../ActionList/actionsRenderHelpers";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type ContextActionBarProps<TContext> = {
	horizontalAlignment?: ActionBarHorizontalAlignment;
	hasBorderBottom?: boolean;
	actions: readonly ActionAndOthers<TContext>[];
	context: TContext;
	trackingId: string;
};

export const ContextActionBar = <TContext,>(props: ContextActionBarProps<TContext>): null | ReactElement => {
	const {
		horizontalAlignment,
		hasBorderBottom,
		actions,
		context: contextFromProps,
		trackingId
	} = props;

	const availableActions = useMemo(
		(): readonly ActionAndOthers<TContext>[] => {
			return reduceToAvailableActions(
				actions,
				contextFromProps
			);
		},
		[actions, contextFromProps]
	);

	const grouped = useMemo(
		(): Partial<Record<string, ActionAndOthers<TContext>[]>> => groupBy(
			availableActions,
			item => item.group?.name || ""
		),
		[availableActions]
	);

	const mapActions = useCallback(
		(
			actionList: readonly ActionAndOthers<TContext>[],
			context: TContext
		): ReactElement[] => {
			const actionGroupList: (ActionAndOthers<TContext> | ActionAndOthers<TContext>[])[] = [];
			const usedGroups = new Set();

			for (const action of actionList) {
				if (!action.group) {
					actionGroupList.push(action);

					continue;
				}

				if (!usedGroups.has(action.group.name)) {
					const actionGroup: ActionAndOthers<TContext>[] = ensureNotNil(
						grouped[action.group.name]
					);

					actionGroupList.push(actionGroup);
					usedGroups.add(action.group.name);
				}
			}

			const result: ReactElement[] = mapGroupsAndActions(actionGroupList, context);

			return result;
		},
		[grouped]
	);

	const [leftSideActions, rightSideActions] = useMemo(
		(): [left: readonly ReactElement[], right: readonly ReactElement[]] => {
			const groupedActions: Partial<Record<ActionBarItemAlignment, readonly ActionAndOthers<TContext>[]>> = groupBy(
				availableActions,
				(x) => x.align ?? ActionBarItemAlignment.left
			);

			const leftSideActions: readonly ReactElement[] = mapActions(
				groupedActions[ActionBarItemAlignment.left] ?? [],
				contextFromProps
			);

			const rightSideActions: readonly ReactElement[] = mapActions(
				groupedActions[ActionBarItemAlignment.right] ?? [],
				contextFromProps
			);

			return [leftSideActions, rightSideActions];
		},
		[
			availableActions,
			contextFromProps,
			mapActions
		]
	);

	return (
		<ActionBar
			trackingId={trackingId}
			horizontalAlignment={horizontalAlignment}
			hasBorderBottom={hasBorderBottom}
		>
			{leftSideActions}
			<ActionBar.SpaceBetween />
			{rightSideActions}
		</ActionBar>
	);
};

ContextActionBar.displayName = "ContextActionBar";

function mapGroupsAndActions<TContext>(
	actions: readonly (ActionAndOthers<TContext> | readonly ActionAndOthers<TContext>[])[],
	context: TContext
): ReactElement[] {
	const result: ReactElement[] = [];

	for (const actionOrActions of actions) {
		if (isArray(actionOrActions)) {
			result.push(
				mapGroupAction(actionOrActions, context)
			);
			continue;
		} else if (actionOrActions.group !== undefined) {
			throw new Error("child actions are not supported at the root");
		} else {
			result.push(
				mapTopAction(actionOrActions, context)
			);
		}
	}

	return result;
}

function mapGroupAction<TContext>(
	actions: readonly ActionAndOthers<TContext>[],
	context: TContext
): ReactElement {
	const [firstAction] = actions;
	const group: ActionGroup = ensureNotNil(firstAction.group);

	switch (group.kind) {
		case "alternative":
			if (actions.length === 1) {
				return mapTopAction(firstAction, context);
			} else {
				return (
					<ActionBar.MenuButton
						key={group.name}
						icon={group.icon}
						text={group.title}
						trackingId={group.name}
					>
						{actions.map((x, index) => (
							<ActionComponent
								key={x.name}
								action={x}
								context={context}
								itemIndex={index}
							/>
						))}
					</ActionBar.MenuButton>
				);
			}
		case "regular":
			return (
				<ActionBar.MenuButton
					key={group.name}
					icon={group.icon}
					text={group.title}
					trackingId={group.name}
				>
					{actions.map((x, index) => (
						<ActionComponent
							key={x.name}
							action={x}
							context={context}
							itemIndex={index}
						/>
					))}
				</ActionBar.MenuButton>
			);
		case "virtual":
			return (
				<>
					{actions.map((x) => mapTopAction(x, context))}
				</>
			);
		default:
			never(group);
	}
}

function mapTopAction<TContext>(
	action: ActionAndOthers<TContext>,
	context: TContext
): ReactElement {

	const text: string = isNotNil(action.title)
		? resolveActionTitle(action.title, context)
		: "";

	switch (action.kind) {
		case ActionKind.Regular:
			return (
				<ActionBar.Button
					key={action.name}
					icon={action.icon}
					text={text}
					onClick={() => action.execute(context!)}
					trackingId={action.name}
				/>
			);
		case ActionKind.Divider:
			return (
				<ActionBar.Divider
					key={action.name}
				/>
			);
		case ActionKind.BusyIndicator:
		case ActionKind.SelectOption:
		case ActionKind.MultiSelectOption:
			throw new Error(`'${action.kind}' is only supported as child action`);
		default:
			never(action);
	}
}

type ActionComponentProps<TContext> = {
	action: ActionAndOthers<TContext>;
	context: TContext;
	itemIndex: number;
};

const ActionComponent = <TContext, >(props: ActionComponentProps<TContext>): ReactElement => {
	const {
		action,
		context,
		itemIndex
	} = props;

	const {
		ref,
		onKeyDown
	} = useKeyboardListNavigationItem({
		itemIndex,
		shouldCancelAfterExecute: true,
		shouldCancelOnTab: true,
		executeKey: [Key.Enter, Key.Space],
		onExecute: (): void => {
			action.execute
				?.(context);
		},
	});

	return actionComponentFactory(
		action,
		context,
		{
			ref,
			onKeyDown,
			tabIndex: -1
		}
	);
};
ActionComponent.displayName = "ActionComponent";
